import { applySortStrategy, getPossibleOrientations, SORT_STRATEGIES, SpatialGrid } from './common.js';

/**
 * Skyline Bottom-Left with Waste Map (SKYLINE-BL-WM)
 *
 * Maintains a proper skyline contour and tracks gaps between pieces of
 * different heights as free rectangles in a waste map. Falls back to
 * waste map rects before opening new sheets.
 */

function createSheet(id, sheetWidth, sheetHeight) {
  return {
    id,
    width: sheetWidth,
    height: sheetHeight,
    pieces: [],
    grid: new SpatialGrid(sheetWidth, sheetHeight),
    // Skyline contour: sorted segments by x
    skyline: [{ x: 0, y: 0, width: sheetWidth }],
    // Waste map: free rects below the skyline
    wasteMap: [],
  };
}

/**
 * Find best skyline position for a piece of size (pw x ph).
 * The piece sits on top of the skyline — its y is the max y of all
 * spanned segments. Score by: waste (gaps below piece), then lowest y, then leftmost x.
 */
function findSkylinePosition(sheet, pw, ph, sheetWidth, sheetHeight, cutWidth) {
  let bestPos = null;
  let bestWaste = Infinity;
  let bestY = Infinity;
  let bestX = Infinity;

  for (let i = 0; i < sheet.skyline.length; i++) {
    const seg = sheet.skyline[i];
    const x = seg.x;

    // Check if piece fits horizontally starting at this segment
    if (x + pw > sheetWidth + 0.001) continue;

    // Find the max y across all spanned segments and compute waste
    let maxY = seg.y;
    let waste = 0;
    let spanWidth = 0;
    let fits = true;
    let j = i;

    while (spanWidth < pw - 0.001 && j < sheet.skyline.length) {
      const s = sheet.skyline[j];
      if (s.y > maxY) maxY = s.y;
      j++;
      spanWidth = (j < sheet.skyline.length ? sheet.skyline[j].x : sheetWidth) - x;
      // Clamp span to piece width
      if (spanWidth > pw) spanWidth = pw;
    }

    // Check vertical fit (with kerf)
    if (maxY + ph + (maxY > 0 ? cutWidth : 0) > sheetHeight + 0.001) continue;

    const pieceY = maxY + (maxY > 0 ? cutWidth : 0);

    // Compute waste: area of gaps between segment tops and piece bottom
    waste = 0;
    let cx = x;
    for (let k = i; k < sheet.skyline.length && cx < x + pw - 0.001; k++) {
      const s = sheet.skyline[k];
      const segEnd = s.x + s.width;
      const overlapStart = Math.max(cx, s.x);
      const overlapEnd = Math.min(x + pw, segEnd);
      if (overlapEnd > overlapStart) {
        const gapHeight = pieceY - (s.y + (s.y > 0 ? cutWidth : 0));
        if (gapHeight > 0) {
          waste += (overlapEnd - overlapStart) * gapHeight;
        }
      }
      cx = segEnd;
    }

    // Score: minimize waste, then lowest y, then leftmost x
    if (waste < bestWaste - 0.001 ||
        (Math.abs(waste - bestWaste) < 0.001 && pieceY < bestY - 0.001) ||
        (Math.abs(waste - bestWaste) < 0.001 && Math.abs(pieceY - bestY) < 0.001 && x < bestX - 0.001)) {
      bestWaste = waste;
      bestY = pieceY;
      bestX = x;
      bestPos = { x, y: pieceY, segIndex: i };
    }
  }

  return bestPos;
}

/**
 * Add waste rects for gaps between segment tops and the piece bottom.
 */
function addWasteRects(sheet, px, py, pw, cutWidth) {
  let cx = px;
  for (let k = 0; k < sheet.skyline.length && cx < px + pw - 0.001; k++) {
    const s = sheet.skyline[k];
    const segEnd = s.x + s.width;
    const overlapStart = Math.max(cx, s.x);
    const overlapEnd = Math.min(px + pw, segEnd);
    if (overlapEnd > overlapStart + 0.001) {
      const gapTop = s.y + (s.y > 0 ? cutWidth : 0);
      const gapHeight = py - gapTop;
      const gapWidth = overlapEnd - overlapStart;
      if (gapHeight > cutWidth && gapWidth > cutWidth) {
        sheet.wasteMap.push({
          x: overlapStart,
          y: gapTop,
          width: gapWidth,
          height: gapHeight - cutWidth,
        });
      }
    }
    cx = segEnd;
  }
}

/**
 * Update skyline contour after placing a piece at (px, py) of size (pw x ph).
 * The skyline rises to py + ph over the range [px, px + pw].
 */
function updateSkyline(sheet, px, py, pw, ph, cutWidth) {
  const newTop = py + ph;
  const pieceLeft = px;
  const pieceRight = px + pw;

  const newSegments = [];

  for (const seg of sheet.skyline) {
    const segLeft = seg.x;
    const segRight = seg.x + seg.width;

    if (segRight <= pieceLeft + 0.001 || segLeft >= pieceRight - 0.001) {
      // Segment entirely outside piece — keep as is
      newSegments.push(seg);
    } else {
      // Segment overlaps with placed piece
      // Left remainder
      if (segLeft < pieceLeft - 0.001) {
        newSegments.push({ x: segLeft, y: seg.y, width: pieceLeft - segLeft - cutWidth });
      }
      // Right remainder
      if (segRight > pieceRight + 0.001) {
        newSegments.push({ x: pieceRight + cutWidth, y: seg.y, width: segRight - pieceRight - cutWidth });
      }
    }
  }

  // Add the new raised segment for the piece
  newSegments.push({ x: pieceLeft, y: newTop, width: pw });

  // Sort by x
  newSegments.sort((a, b) => a.x - b.x);

  // Filter out invalid segments (width <= 0)
  const filtered = newSegments.filter(s => s.width > 0.001);

  // Merge adjacent same-height segments
  const merged = [];
  for (const seg of filtered) {
    if (merged.length > 0) {
      const prev = merged[merged.length - 1];
      if (Math.abs(prev.y - seg.y) < 0.001 &&
          Math.abs(prev.x + prev.width + cutWidth - seg.x) < 0.001) {
        prev.width = prev.width + cutWidth + seg.width;
        continue;
      } else if (Math.abs(prev.y - seg.y) < 0.001 &&
                 Math.abs(prev.x + prev.width - seg.x) < 0.001) {
        prev.width += seg.width;
        continue;
      }
    }
    merged.push({ ...seg });
  }

  sheet.skyline = merged;
}

/**
 * Check if placing a piece at (px, py) with size (pw, ph) would overlap
 * any existing piece on the sheet, respecting cutWidth (kerf).
 * Uses same logic as validateLayouts: pieces must be separated by at least cutWidth.
 */
function hasOverlap(sheet, px, py, pw, ph, cutWidth) {
  const EPS = 0.001;
  // Use spatial grid for candidate narrowing
  const candidates = sheet.grid.query(px - cutWidth - EPS, py - cutWidth - EPS, pw + 2 * cutWidth + 2 * EPS, ph + 2 * cutWidth + 2 * EPS);
  for (const p of candidates) {
    if (!(px + pw + cutWidth <= p.x + EPS ||
          p.x + p.width + cutWidth <= px + EPS ||
          py + ph + cutWidth <= p.y + EPS ||
          p.y + p.height + cutWidth <= py + EPS)) {
      return true;
    }
  }
  return false;
}

/**
 * Find best fit in the waste map using BSSF (Best Short Side Fit).
 * Validates against existing pieces to prevent overlaps.
 */
function findWasteMapPosition(sheet, pw, ph, cutWidth) {
  let bestIdx = -1;
  let bestShortSide = Infinity;
  let bestLongSide = Infinity;

  for (let i = 0; i < sheet.wasteMap.length; i++) {
    const rect = sheet.wasteMap[i];
    if (pw <= rect.width + 0.001 && ph <= rect.height + 0.001) {
      // Validate no overlap with existing pieces
      if (hasOverlap(sheet, rect.x, rect.y, pw, ph, cutWidth)) continue;

      const leftoverH = rect.width - pw;
      const leftoverV = rect.height - ph;
      const shortSide = Math.min(leftoverH, leftoverV);
      const longSide = Math.max(leftoverH, leftoverV);
      if (shortSide < bestShortSide - 0.001 ||
          (Math.abs(shortSide - bestShortSide) < 0.001 && longSide < bestLongSide - 0.001)) {
        bestShortSide = shortSide;
        bestLongSide = longSide;
        bestIdx = i;
      }
    }
  }

  if (bestIdx >= 0) {
    const rect = sheet.wasteMap[bestIdx];
    // Remove the used waste rect
    sheet.wasteMap.splice(bestIdx, 1);
    // Add remainders as new waste rects (guillotine split)
    const rightW = rect.width - pw - cutWidth;
    const bottomH = rect.height - ph - cutWidth;
    if (rightW > cutWidth && bottomH > cutWidth) {
      if (rightW < bottomH) {
        sheet.wasteMap.push({ x: rect.x + pw + cutWidth, y: rect.y, width: rightW, height: rect.height });
        sheet.wasteMap.push({ x: rect.x, y: rect.y + ph + cutWidth, width: pw, height: bottomH });
      } else {
        sheet.wasteMap.push({ x: rect.x, y: rect.y + ph + cutWidth, width: rect.width, height: bottomH });
        sheet.wasteMap.push({ x: rect.x + pw + cutWidth, y: rect.y, width: rightW, height: ph });
      }
    } else if (rightW > cutWidth) {
      sheet.wasteMap.push({ x: rect.x + pw + cutWidth, y: rect.y, width: rightW, height: rect.height });
    } else if (bottomH > cutWidth) {
      sheet.wasteMap.push({ x: rect.x, y: rect.y + ph + cutWidth, width: rect.width, height: bottomH });
    }
    return { x: rect.x, y: rect.y };
  }

  return null;
}

/**
 * Try to place a piece on a sheet: skyline first, then waste map fallback.
 * Both placements are validated against existing pieces to prevent overlaps.
 */
function tryPlace(sheet, orient, sheetWidth, sheetHeight, cutWidth) {
  // Try skyline position first
  const skyPos = findSkylinePosition(sheet, orient.width, orient.height, sheetWidth, sheetHeight, cutWidth);
  if (skyPos && !hasOverlap(sheet, skyPos.x, skyPos.y, orient.width, orient.height, cutWidth)) {
    return { x: skyPos.x, y: skyPos.y, source: 'skyline' };
  }

  // Fall back to waste map (already validates overlap internally)
  const wmPos = findWasteMapPosition(sheet, orient.width, orient.height, cutWidth);
  if (wmPos) {
    return { x: wmPos.x, y: wmPos.y, source: 'wastemap' };
  }

  return null;
}

/**
 * Skyline-WM optimization algorithm
 */
export const skylineWMOptimize = (allPieces, sheetWidth, sheetHeight, cutWidth, sortStrategy) => {
  const sorted = sortStrategy
    ? applySortStrategy(allPieces, sortStrategy)
    : applySortStrategy(allPieces, SORT_STRATEGIES.DESCSS);

  const sheets = [createSheet(1, sheetWidth, sheetHeight)];
  const unplacedPieces = [];

  for (const piece of sorted) {
    const orientations = getPossibleOrientations(piece);
    let placed = false;

    // Try each orientation on each existing sheet
    for (const orient of orientations) {
      if (placed) break;
      for (const sheet of sheets) {
        const pos = tryPlace(sheet, orient, sheetWidth, sheetHeight, cutWidth);
        if (pos) {
          const placedPiece = { ...orient, x: pos.x, y: pos.y };
          sheet.pieces.push(placedPiece);
          sheet.grid.insert(placedPiece);
          if (pos.source === 'skyline') {
            addWasteRects(sheet, pos.x, pos.y, orient.width, cutWidth);
            updateSkyline(sheet, pos.x, pos.y, orient.width, orient.height, cutWidth);
          }
          placed = true;
          break;
        }
      }
    }

    if (!placed) {
      // Try new sheet
      const newSheet = createSheet(sheets.length + 1, sheetWidth, sheetHeight);
      for (const orient of orientations) {
        const pos = tryPlace(newSheet, orient, sheetWidth, sheetHeight, cutWidth);
        if (pos) {
          const placedPiece = { ...orient, x: pos.x, y: pos.y };
          newSheet.pieces.push(placedPiece);
          newSheet.grid.insert(placedPiece);
          if (pos.source === 'skyline') {
            addWasteRects(newSheet, pos.x, pos.y, orient.width, cutWidth);
            updateSkyline(newSheet, pos.x, pos.y, orient.width, orient.height, cutWidth);
          }
          sheets.push(newSheet);
          placed = true;
          break;
        }
      }
      if (!placed) {
        unplacedPieces.push(piece);
      }
    }
  }

  const layouts = sheets
    .filter(s => s.pieces.length > 0)
    .map((s, idx) => ({
      id: idx + 1,
      width: s.width,
      height: s.height,
      pieces: s.pieces,
    }));

  return { layouts, unplacedPieces, unplacedCount: unplacedPieces.length };
};
